import html2canvas from "html2canvas";
import { jsPDF as JsPdf } from "jspdf";
import { DateTime } from "luxon";
import { LOCALE } from "./constants";

const A4_WIDTH_MM = 210;
const A4_HEIGHT_MM = 297;
const MARGIN_MM = 10;
const MARGIN_TOTAL_MM = MARGIN_MM * 2;
const HEADER_HEIGHT_MM = 22;

export interface IPdfExportOptions {
  title: string;
  // Resumen de filtros activos (rango de fechas + dimensiones), ya legible.
  subtitle: string;
  fileName: string;
}

// Dibuja el encabezado del reporte en la primera página: nombre, filtros activos
// y fecha de generación. Devuelve el offset vertical donde arranca el contenido.
const drawHeader = (pdf: JsPdf, options: IPdfExportOptions): number => {
  const generatedAt = DateTime.now()
    .setLocale(LOCALE)
    .toFormat("dd/MM/yyyy HH:mm");

  pdf.setFontSize(14);
  pdf.setTextColor(33, 33, 33);
  pdf.text(options.title, MARGIN_MM, MARGIN_MM + 4);

  pdf.setFontSize(9);
  pdf.setTextColor(100, 100, 100);
  pdf.text(options.subtitle, MARGIN_MM, MARGIN_MM + 11);
  pdf.text(`Generado: ${generatedAt}`, MARGIN_MM, MARGIN_MM + 16);

  return MARGIN_MM + HEADER_HEIGHT_MM;
};

// Pagina una imagen alta (más que una página A4) desplazándola hacia arriba en
// cada página sucesiva. El encabezado solo va en la primera página.
const addPaginatedImage = (
  pdf: JsPdf,
  imgData: string,
  imgHeightMm: number,
  firstOffsetMm: number,
): void => {
  const contentWidth = A4_WIDTH_MM - MARGIN_TOTAL_MM;
  const firstPageContent = A4_HEIGHT_MM - firstOffsetMm - MARGIN_MM;

  pdf.addImage(imgData, "PNG", MARGIN_MM, firstOffsetMm, contentWidth, imgHeightMm);
  let heightLeft = imgHeightMm - firstPageContent;

  while (heightLeft > 0) {
    pdf.addPage();
    const position = MARGIN_MM - (imgHeightMm - heightLeft);
    pdf.addImage(imgData, "PNG", MARGIN_MM, position, contentWidth, imgHeightMm);
    heightLeft -= A4_HEIGHT_MM - MARGIN_TOTAL_MM;
  }
};

// Exporta el dashboard renderizado a PDF (client-side). Captura el DOM tal cual
// se ve —incluye los gráficos Canvas de Chart.js— respetando los filtros
// vigentes (el estado actual del árbol). Ver PLAN_MAESTRO_DASHBOARD_METRICAS §B.
export const exportDashboardPdf = async (
  element: HTMLElement,
  options: IPdfExportOptions,
): Promise<void> => {
  const canvas = await html2canvas(element, {
    scale: 2,
    backgroundColor: "#ffffff",
    useCORS: true,
    windowWidth: element.scrollWidth,
  });

  const pdf = new JsPdf("p", "mm", "a4");
  const contentWidth = A4_WIDTH_MM - MARGIN_TOTAL_MM;
  const imgHeightMm = (canvas.height * contentWidth) / canvas.width;

  const contentOffset = drawHeader(pdf, options);
  addPaginatedImage(
    pdf,
    canvas.toDataURL("image/png"),
    imgHeightMm,
    contentOffset,
  );

  pdf.save(options.fileName);
};
